/**
 * Refund Inspection Fee Modal
 *
 * Modal for refunding inspection fee to buyer's wallet
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BaseActionModal, { IActionModalProps, IModalChildProps } from './BaseActionModal';
import { ArbitrationAction, IFinancialInfo } from '../../../services/arbitrationService';

interface RefundInspectionFeeModalProps extends Omit<IActionModalProps, 'action'> {
  financial?: IFinancialInfo;
}

const RefundInspectionFeeContent: React.FC<IModalChildProps & { financial?: IFinancialInfo }> = ({
  setPayload,
  setIsValid,
  financial
}) => {
  const [reason, setReason] = useState('');
  const [refundInspectionFee, setRefundInspectionFee] = useState(true);
  const inspectionFee = financial?.inspectionFee || 0;
  const inspectorShippingCost = financial?.inspectorShippingCost || 0;
  const totalRefund = (refundInspectionFee ? inspectionFee : 0) + inspectorShippingCost;

  useEffect(() => {
    setPayload({
      reason,
      refundInspectionFee,
      amount: totalRefund
    });
    setIsValid(reason.length >= 5);
  }, [reason, refundInspectionFee, totalRefund, setPayload, setIsValid]);

  return (
    <Box>
      <Alert severity="info" icon={<ReceiptLongIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          هزینه بازرسی و ارسال به بازرس به کیف پول خریدار بازگردانده می‌شود.
        </Typography>
      </Alert>

      {/* Financial Summary */}
      <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>جزئیات بازپرداخت</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">هزینه بازرسی:</Typography>
          <Typography variant="body2">{inspectionFee.toLocaleString()} تومان</Typography>
        </Box>
        {inspectorShippingCost > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">هزینه ارسال به بازرس:</Typography>
            <Typography variant="body2">{inspectorShippingCost.toLocaleString()} تومان</Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" fontWeight="bold">مجموع بازپرداخت:</Typography>
          <Typography variant="body2" fontWeight="bold" color="primary.main">
            {totalRefund.toLocaleString()} تومان
          </Typography>
        </Box>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={refundInspectionFee}
            onChange={(e) => setRefundInspectionFee(e.target.checked)}
            color="info"
          />
        }
        label="بازپرداخت هزینه بازرسی"
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        multiline
        rows={2}
        label="دلیل بازپرداخت"
        placeholder="مثال: نتیجه بازرسی نادرست بوده و خریدار حق دارد"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        helperText="حداقل ۵ کاراکتر"
      />
    </Box>
  );
};

const RefundInspectionFeeModal: React.FC<RefundInspectionFeeModalProps> = ({ financial, ...props }) => {
  return (
    <BaseActionModal {...props} action={ArbitrationAction.REFUND_INSPECTION_FEE}>
      <RefundInspectionFeeContent
        financial={financial}
        payload={{}}
        setPayload={() => {}}
        isValid={false}
        setIsValid={() => {}}
      />
    </BaseActionModal>
  );
};

export default RefundInspectionFeeModal;
